import { useEffect, useState } from 'react';
import { fetchProtectedAssetBlobUrl } from '../lib/api.js';

const CACHE_LIMIT = 96;
const imageBlobCache = new Map();

function touchCacheEntry(key, entry) {
  entry.lastUsedAt = Date.now();
  imageBlobCache.delete(key);
  imageBlobCache.set(key, entry);
}

function evictCacheIfNeeded() {
  while (imageBlobCache.size > CACHE_LIMIT) {
    const oldestKey = imageBlobCache.keys().next().value;
    if (!oldestKey) break;
    const oldestEntry = imageBlobCache.get(oldestKey);
    if (oldestEntry?.blobUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(oldestEntry.blobUrl);
    }
    imageBlobCache.delete(oldestKey);
  }
}

async function resolveCachedBlobUrl(src) {
  const existing = imageBlobCache.get(src);
  if (existing?.blobUrl) {
    touchCacheEntry(src, existing);
    return existing.blobUrl;
  }

  if (existing?.promise) {
    return existing.promise;
  }

  const promise = fetchProtectedAssetBlobUrl(src)
    .then((blobUrl) => {
      const resolved = { blobUrl, lastUsedAt: Date.now() };
      imageBlobCache.set(src, resolved);
      evictCacheIfNeeded();
      return blobUrl;
    })
    .catch((error) => {
      imageBlobCache.delete(src);
      throw error;
    });

  imageBlobCache.set(src, { promise, lastUsedAt: Date.now() });
  return promise;
}

export default function ProtectedImage({ src, alt = '', className = '', style, fallback = null, ...props }) {
  const [resolvedSrc, setResolvedSrc] = useState(() => (/^https?:\/\//i.test(src || '') ? src : ''));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;

    async function load() {
      if (!src) {
        setResolvedSrc('');
        setFailed(false);
        return;
      }

      if (/^https?:\/\//i.test(src)) {
        setResolvedSrc(src);
        setFailed(false);
        return;
      }

      try {
        const blobUrl = await resolveCachedBlobUrl(src);
        if (!active) return;
        setResolvedSrc(blobUrl);
        setFailed(false);
      } catch {
        if (!active) return;
        setResolvedSrc('');
        setFailed(true);
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [src]);

  if (!src) return fallback;
  if (failed || !resolvedSrc) return fallback;

  return <img src={resolvedSrc} alt={alt} className={className} style={style} {...props} />;
}
